import cx from "classnames";
import { useState } from "react";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { EditableText, Flex, Text, Menu, Icon, Button } from "@theorgchartdev/storybook";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";
import { GetWSMgr } from "@/Utils/StateLib";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { appstr } from "@coreapp/utils/appstr";
import { DeletePlanPopover } from "./DeletePlanPopover";
import { SubmissionStatus } from "@/Utils/Plan/PlanSubmisionsTypes";

export const PlanNameText = ({
  rowState,
  onRename,
  handlePlanAction,
  selectedPlan,
  dataTestId
}) => {
  const {
    shareContextValue: { setShareData, openShareModal }
  } = useWSMgrContext();

  // This flag ensures that when the user hovers off the row, the additional options menu remains open
  const [keepActive, setKeepActive] = useState(false);

  const iconProps = { size: "small", layout: "default" };
  const rowData = rowState?.data;
  const planName = rowData?.name;
  const planID = rowData?.ID;
  const owner = rowData?.owner;
  const users = rowData?.users;
  const emailInvite = rowData?.emailInvite;
  const defaultRole = rowData?.defaultRole;

  const {
    canDelete,
    canDuplicate,
    canManageFields,
    canManageStatus,
    canRename,
    canShare,
    canViewAbout
  } = getPlanPermissions({
    plan: rowData
  });

  const onSavePlanUsers = data => {
    const { users, emailInvite, defaultRole } = data;
    let targetPlan = rowData;
    targetPlan["users"] = users;
    targetPlan["emailInvite"] = emailInvite;
    if (defaultRole) {
      targetPlan["defaultRole"] = defaultRole;
    }
    handlePlanAction(targetPlan, "updateplanusers");
  };

  const OpenShareModal = () => {
    setShareData({
      usersWithAccess: users,
      owner: owner,
      name: planName,
      docId: planID,
      emailInvite: emailInvite,
      defaultRole: defaultRole
    });
    openShareModal(onSavePlanUsers);
  };

  const isSelected = planID === selectedPlan;
  const baseTextClass = "ocn-folder-row-text";
  const activeTextClass = isSelected ? "ocn-file-row-active-text" : null;

  const BuildAdditionalOptionsMenu = () => {
    const additionalOptions = [];

    if (canManageFields) {
      // Temporarily disable this action as currently opened doc is used to calculated fields for PlanFieldsModal from Plan Explorer.
      // TODO: Will be handled later
      // additionalOptions.push({
      //   label: appstr("Manage plan fields"),
      //   value: 5,
      //   clickHandler: () => handlePlanAction(rowData, "editfields"),
      //   dataTestId: dataTestId + "-editfields",
      //   icon: "gear"
      // });
    }

    if (canManageStatus) {
      additionalOptions.push({
        label: rowData?.status === "Archived" ? appstr("Restore plan") : appstr("Archive Plan"),
        value: 1,
        clickHandler: () => handlePlanAction(rowData, "archive"),
        dataTestId: dataTestId + "-archive",
        icon: "box-archive"
      });

      additionalOptions.push({
        label: rowData?.status === "Locked" ? appstr("Unlock Plan") : appstr("Lock Plan"),
        value: 2,
        clickHandler: () => handlePlanAction(rowData, "toggleLock"),
        dataTestId: dataTestId + "-lock",
        icon: rowData?.status === "Locked" ? "lock-open" : "lock",
        divider: true
      });
    }

    if (canViewAbout) {
      additionalOptions.push({
        label: appstr("About Plan"),
        value: 4,
        clickHandler: () => handlePlanAction(rowData, "about"),
        dataTestId: dataTestId + "-about",
        icon: "circle-info"
      });
    }

    const wsMgr = GetWSMgr();
    if (wsMgr.fAdminLogin) {
      additionalOptions.push({
        label: appstr("Download plan"),
        value: 3,
        clickHandler: () => handlePlanAction(rowData, "download"),
        dataTestId: dataTestId + "-download",
        icon: "arrow-down-to-line"
      });
    }

    return additionalOptions;
  };

  const planMenuTrigger = (
    <Button
      dataTestId={`${dataTestId}-addt-options`}
      style={{
        padding: 0
      }}
      modifier="tertiary"
      iconOnly
      iconProps={{
        name: "ellipsis-vertical"
      }}
    />
  );

  const additionalOptions = BuildAdditionalOptionsMenu();

  const additonalOptionsMenu = (
    <Menu
      popoverProps={{ onOpen: () => setKeepActive(true), onClose: () => setKeepActive(false) }}
      fillWidth={false}
      trigger={planMenuTrigger}
      options={additionalOptions}
      position={"bottom-end"}
      optionSize={"medium"}
      listWidth={"medium"}
    />
  );

  const BuildPlanActions = () => {
    const actions = [];

    if (additionalOptions.length > 0) {
      actions.push({
        content: additonalOptionsMenu
      });
    }

    if (canDuplicate) {
      actions.push({
        iconName: "copy",
        iconProps: {
          ...iconProps
        },
        tooltipProps: {
          message: appstr("Copy entity", {
            entity: appstr("Plan").toLowerCase()
          })
        },
        onClick: () => handlePlanAction(rowData, "duplicateplan"),
        dataTestId: dataTestId + "-duplicate"
      });
    }

    if (canShare) {
      actions.push({
        iconName: "share-nodes",
        iconProps: { ...iconProps },
        tooltipProps: { message: appstr("Share plan") },
        onClick: () => OpenShareModal(),
        dataTestId: dataTestId + "-share"
      });
    }

    if (canDelete) {
      actions.push({
        tooltipProps: { message: appstr("Delete plan") },
        content: (
          <DeletePlanPopover
            handleDelete={() => handlePlanAction(rowData, "deleteplan")}
            dataTestId={dataTestId + "-delete"}
            onPopoverOpen={() => setKeepActive(true)}
            onPopoverClose={() => setKeepActive(false)}
          />
        )
      });
    }

    return actions;
  };

  const textContent = canRename ? (
    <EditableText
      className="oc-fill-width"
      size={"small"}
      bold={false}
      text={planName}
      onChange={onRename}
      eventInfo={{ renamePlanID: planID }}
      customClass={cx(baseTextClass, activeTextClass)}
    />
  ) : (
    <Text size={"small"}>{planName}</Text>
  );

  return (
    <Flex>
      <ActionListItem
        dataTestId={dataTestId}
        contentBefore={textContent}
        visible={keepActive ? "always" : "hover"}
        actions={BuildPlanActions()}
      />
    </Flex>
  );
};
